import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import useStore from "../store.js";
import ModalPayCheck from "./Modal/ModalPayCheck.jsx";

const makeRequestId = () => {
  const cryptoApi = window.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const values = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(values);
  } else {
    values.forEach((_, index) => {
      values[index] = Math.floor(Math.random() * 256);
    });
  }
  values[6] = (values[6] & 0x0f) | 0x40;
  values[8] = (values[8] & 0x3f) | 0x80;
  const hex = Array.from(values, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const incompleteEvidenceEnding = /(?:[.!?。！？]|했다|하였다|되었다|된다|됐다|있다|없다|않다|이다|였다|한다|입니다|합니다|됩니다|했습니다|있어요|없어요|했어요|돼요|으나|지만|이지만|는데|았는데|었는데|였는데|으며|이며|면서|하여|해서|하고|이고|되어|돼서|므로|기에|다가|도록|않아|이라서|하는|되는|있는|없는|같은|맞는|된|관한|관련한)$/;

const isConciseEvidencePhrase = (value, maxLength) => {
  const normalized = value?.trim();
  return Boolean(
    normalized
    && normalized.length <= maxLength
    && !incompleteEvidenceEnding.test(normalized),
  );
};

const renderHeadline = (headline = "", emphasis = "") => {
  const normalizedEmphasis = emphasis.trim();
  const emphasisIndex = normalizedEmphasis
    ? headline.indexOf(normalizedEmphasis)
    : -1;

  if (emphasisIndex < 0) return headline;

  return (
    <>
      {headline.slice(0, emphasisIndex)}
      <SummaryEmphasis>{normalizedEmphasis}</SummaryEmphasis>
      {headline.slice(emphasisIndex + normalizedEmphasis.length)}
    </>
  );
};

const ResultSummaryPreview = ({ resultId, preview, caseCount = 5, inquiries = [] }) => {
  const navigate = useNavigate();
  const startRefinementSSE = useStore((state) => state.startRefinementSSE);
  const [answers, setAnswers] = useState({});
  const [showPayment, setShowPayment] = useState(false);
  const [requestId, setRequestId] = useState("");

  const data = preview?.data || null;
  const summary = data?.summary || null;
  const evidence = (data?.evidence || []).filter(
    (item) => isConciseEvidencePhrase(item?.name, 28)
      && isConciseEvidencePhrase(item?.importance, 32),
  );
  const questions = useMemo(() => data?.self_check_questions || [], [data]);
  const allAnswered = questions.length > 0 && questions.every((question) => answers[question.id]);
  const normalizedCaseCount = Math.max(1, caseCount || 5);
  const sources = (summary?.sources || []).filter((source, index, all) => (
    Number.isInteger(source?.case_index)
    && source.case_index >= 1 && source.case_index <= normalizedCaseCount
    && typeof source.quote === "string" && source.quote.trim()
    && all.findIndex((item) => item.case_index === source.case_index && item.quote === source.quote) === index
  ));
  // A claim dismissal must not be presented as a confirmed payment obligation.
  // This catches the observed conflict only; it does not validate legal meaning.
  const hasVerdictConflict = sources.some((source) => {
    const answer = String(inquiries[source.case_index - 1]?.answer || "").replace(/\s/g, "");
    const quote = source.quote.replace(/\s/g, "");
    return /원고(?:의)?청구(?:를|는)?(?:모두)?기각/.test(answer)
      && !/지급|반환|인용/.test(answer)
      && /(?:지급|반환)할의무가있다(?:[.!。]|$)/.test(quote);
  });
  const sourceProof = (source) => {
    const item = inquiries[source.case_index - 1];
    return (
      <SourceItem key={`${source.case_index}-${source.quote}`}>
        <a href={`#case-result-${source.case_index}`}>
          {source.case_index}번 판례{item?.caseNumber ? ` · ${item.courtName || ""} ${item.caseNumber}` : " 원문"}
        </a>
        {!hasVerdictConflict && <blockquote>{source.quote}</blockquote>}
      </SourceItem>
    );
  };

  useEffect(() => {
    setAnswers({});
    setRequestId("");
    setShowPayment(false);
  }, [resultId]);

  const refinementAnswers = useMemo(
    () => questions.map((question) => ({
      question_id: question.id,
      answer: answers[question.id],
    })),
    [answers, questions],
  );

  const openPayment = () => {
    if (!allAnswered) return;
    setRequestId((current) => current || makeRequestId());
    setShowPayment(true);
  };

  const runRefinement = async () => {
    setShowPayment(false);
    const stableRequestId = requestId || makeRequestId();
    if (!requestId) setRequestId(stableRequestId);
    await startRefinementSSE(
      resultId,
      refinementAnswers,
      stableRequestId,
      (newLogId) => navigate(`/main/q/${newLogId}`, { replace: true }),
      () => navigate("/main/q/waiting"),
      questions.map((question) => ({
        question: question.question,
        answer: answers[question.id],
      })),
    );
  };

  return (
    <Root>
      <AnalysisColumn>
        <SummarySurface>
          <SectionHeader>
            <SummaryHeading>
              {hasVerdictConflict ? "요약과 판례 결론이 달라요" : "비슷한 상황, 법원은 이렇게 판단했습니다"}
            </SummaryHeading>
            <SummaryDescription>
              {hasVerdictConflict
                ? "아래 근거를 눌러 판례의 실제 주문을 확인해 주세요."
                : summary?.basis === "single_case"
                ? "이 판례에서 판단을 가른 조건을 먼저 짚었습니다."
                : `상위 ${normalizedCaseCount}개 판례에서 비슷한 조건과 실제 판단을 짚었습니다.`}
            </SummaryDescription>
          </SectionHeader>

          {summary ? (
            <SummaryCard>
              {!hasVerdictConflict && (
                <SummaryHeadline data-testid="analysis-headline">
                  {renderHeadline(summary.headline, summary.emphasis)}
                </SummaryHeadline>
              )}
              {sources.length > 0 && (
                <SourceProof $standalone={hasVerdictConflict} role="region" aria-label="요약의 판례 원문 근거">
                  {sourceProof(sources[0])}
                  {sources.length > 1 && (
                    <details>
                      <summary>나머지 근거 {sources.length - 1}개 보기</summary>
                      {sources.slice(1).map(sourceProof)}
                    </details>
                  )}
                </SourceProof>
              )}
            </SummaryCard>
          ) : (
            <EmptyAnalysis role="status">
              판단을 뒷받침할 원문이 부족합니다. 아래 판례에서 내 상황과 다른 조건을 확인해 보세요.
            </EmptyAnalysis>
          )}
        </SummarySurface>

        {evidence.length > 0 && (
          <Section>
            <CompactSectionTitle>판례에서 중요하게 본 증거</CompactSectionTitle>
            <EvidenceCard data-testid="evidence-card">
              {evidence.map((item) => (
                <EvidenceRow
                  key={`${item.name}-${item.importance}`}
                  data-testid="evidence-row"
                >
                  <EvidenceBullet aria-hidden="true" />
                  <EvidenceBody>
                    <EvidenceMain>
                      <EvidenceName>{item.name}</EvidenceName>
                      <EvidenceImportance>{item.importance}</EvidenceImportance>
                    </EvidenceMain>
                  </EvidenceBody>
                </EvidenceRow>
              ))}
            </EvidenceCard>
          </Section>
        )}

        {questions.length > 0 && (
          <Section data-testid="self-check-section">
            <CompactSectionTitle>질문을 업데이트할 정보가 있나요?</CompactSectionTitle>
            <SectionDescription>
              아래 질문에 답하면 내 상황과 더 가까운 판례를 다시 찾아드려요.
            </SectionDescription>
            <QuestionCard data-testid="question-card">
              {questions.map((question) => (
                <QuestionRow key={question.id}>
                  <QuestionBody>
                    <QuestionText>{question.question}</QuestionText>
                  </QuestionBody>
                  <AnswerGroup aria-label={`${question.question} 답변`}>
                    {[
                      ["yes", "예"],
                      ["no", "아니오"],
                    ].map(([value, label]) => (
                      <AnswerButton
                        key={value}
                        type="button"
                        $selected={answers[question.id] === value}
                        $wide={value === "no"}
                        aria-pressed={answers[question.id] === value}
                        onClick={() => setAnswers((current) => ({
                          ...current,
                          [question.id]: value,
                        }))}
                      >
                        {label}
                      </AnswerButton>
                    ))}
                  </AnswerGroup>
                </QuestionRow>
              ))}
              <RefineButton type="button" disabled={!allAnswered} onClick={openPayment}>
                질문 업데이트하고 결과 다시 보기
              </RefineButton>
            </QuestionCard>
          </Section>
        )}

        <Disclaimer>
          AI 요약에는 오류가 있을 수 있습니다. 정확한 판단은 판례 원문과 전문가 검토가 필요합니다.
        </Disclaimer>

        <CasesHeading>
          <CompactSectionTitle>유사 판례 {caseCount}개</CompactSectionTitle>
          <CasesDescription>
            요약의 근거가 된 판례를 아래에서 확인할 수 있습니다.
          </CasesDescription>
        </CasesHeading>
      </AnalysisColumn>

      <ModalPayCheck
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onConfirm={runRefinement}
        law={10}
        buttonType="queryButton"
        headerContent="질문 업데이트하고 결과 다시 보기"
        additionalContent="선택한 답변을 내 상황에 반영해 판례를 다시 찾고, 새 결과는 이전 결과와 따로 저장됩니다."
        checkCreditBeforeConfirm={false}
      />
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: Pretendard, sans-serif;
`;

const AnalysisColumn = styled.div`
  width: min(680px, 100%);

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 14px;
`;

const SummaryHeading = styled.h2`
  margin: 0;
  color: rgba(0, 0, 0, 0.78);
  font-size: 16px;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.32px;
`;

const SummaryDescription = styled.p`
  margin: 2px 0 0;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.26px;
`;

const SummarySurface = styled.section`
  padding: 20px 22px;
  border-radius: 24px;
  background: #f9f9f9;

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 20px;
  }
`;

const SummaryCard = styled.div`
  padding: 18px;
  border: 1px solid #1ae276;
  border-radius: 16px;
  background: #fff;
`;

const SummaryHeadline = styled.h3`
  margin: 0;
  color: #141414;
  font-size: 15px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.3px;
`;

const SummaryEmphasis = styled.strong`
  font-weight: 700;
`;

const SourceProof = styled.div`
  margin-top: ${({ $standalone }) => ($standalone ? "0" : "16px")};
  border-top: ${({ $standalone }) => ($standalone ? "none" : "1px solid #e7e9ee")};
  padding-top: ${({ $standalone }) => ($standalone ? "0" : "12px")};
  font-size: 13px;
  line-height: 21px;
  overflow-wrap: anywhere;

  summary {
    cursor: pointer;
    color: #376854;
    padding: 8px 0;
  }
`;

const SourceItem = styled.div`
  & + & { margin-top: 12px; }

  a {
    color: #376854;
    font-weight: 600;
    text-underline-offset: 3px;
  }

  blockquote {
    margin: 6px 0 0;
    padding-left: 10px;
    border-left: 2px solid #1ae276;
    color: #444;
  }
`;

const Section = styled.section`
  margin-top: 36px;
`;

const CompactSectionTitle = styled.h3`
  margin: 0;
  color: rgba(0, 0, 0, 0.78);
  font-size: 16px;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.32px;
`;

const SectionDescription = styled.p`
  margin: 2px 0 12px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.26px;
  word-break: keep-all;
  overflow-wrap: break-word;
`;

const EvidenceCard = styled.div`
  margin-top: 8px;
  padding: 9px 18px;
  border: 1px solid #e7e9ee;
  border-radius: 24px;
  background: #fff;

  @media (max-width: 480px) {
    padding: 9px 14px;
    border-radius: 24px;
  }
`;

const EvidenceRow = styled.div`
  min-height: 36px;
  display: grid;
  grid-template-columns: 7px minmax(0, 1fr);
  gap: 12px;
  align-items: center;

  & + & {
    margin-top: 4px;
  }
`;

const EvidenceBullet = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #1ae276;
`;

const EvidenceBody = styled.div`
  min-width: 0;
`;

const EvidenceMain = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 210px) minmax(0, 1fr);
  grid-template-areas: "name importance";
  gap: 12px;
  align-items: center;

  @media (max-width: 480px) {
    grid-template-columns: minmax(110px, 128px) minmax(0, 1fr);
    grid-template-areas: "name importance";
    gap: 12px;
  }
`;

const EvidenceName = styled.div`
  grid-area: name;
  color: rgba(0, 0, 0, 0.74);
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
`;

const EvidenceImportance = styled.div`
  grid-area: importance;
  color: rgba(0, 0, 0, 0.5);
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
`;

const QuestionCard = styled.div`
  padding: 15px 13px;
  border: 1px solid #e7e9ee;
  border-radius: 24px;
  background: #f9f9f9;
`;

const QuestionRow = styled.div`
  min-height: 58px;
  box-sizing: border-box;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  border: 1px solid #e7e9ee;
  border-radius: 16px;
  background: #fff;

  & + & {
    margin-top: 12px;
  }

  @media (max-width: 480px) {
    padding: 12px 14px;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
  }
`;

const QuestionBody = styled.div`
  min-width: 0;
`;

const QuestionText = styled.div`
  color: #171a1f;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  word-break: keep-all;
  overflow-wrap: break-word;
`;

const AnswerGroup = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const AnswerButton = styled.button`
  width: ${(props) => (props.$wide ? "60px" : "40px")};
  min-width: ${(props) => (props.$wide ? "60px" : "40px")};
  height: 32px;
  padding: 0;
  border: 1px solid ${(props) => (props.$selected ? "#00bd59" : "#dfe2e7")};
  border-radius: 999px;
  background: ${(props) => (props.$selected ? "#e6fff2" : "#fff")};
  color: ${(props) => (props.$selected ? "#008c42" : "rgba(0, 0, 0, 0.5)")};
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #0057ff;
    outline-offset: 2px;
  }

`;

const RefineButton = styled.button`
  width: min(300px, 100%);
  min-height: 42px;
  margin: 16px auto 0;
  padding: 8px 16px;
  display: block;
  border: 0;
  border-radius: 8px;
  background: ${(props) => (props.disabled
    ? "#e8e8e8"
    : "linear-gradient(92deg, #00f370 0%, #00badc 50.5%, #0057ff 100%)")};
  color: ${(props) => (props.disabled ? "rgba(0, 0, 0, 0.3)" : "#fff")};
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const EmptyAnalysis = styled.div`
  padding: 18px 20px;
  border: 1px solid #e7e9ee;
  border-radius: 16px;
  color: rgba(0, 0, 0, 0.5);
  font-size: 13px;
  font-weight: 500;
  line-height: 21px;
`;

const Disclaimer = styled.aside`
  margin-top: 24px;
  padding: 18px 20px;
  border: 1px solid #e7e9ee;
  border-radius: 20px;
  color: rgba(0, 0, 0, 0.46);
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  word-break: keep-all;
  overflow-wrap: break-word;
`;

const CasesHeading = styled.div`
  margin-top: 40px;
  margin-bottom: 16px;
`;

const CasesDescription = styled.p`
  margin: 2px 0 0;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
`;

export default ResultSummaryPreview;
